/* eslint-disable filenames/match-regex */

import { GlyphProps } from '@atlaskit/icon';
import ArrowLeftCircleIcon from '@atlaskit/icon/glyph/arrow-left-circle';
import CalendarFilledIcon from '@atlaskit/icon/glyph/calendar-filled';
import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import CheckboxIcon from '@atlaskit/icon/glyph/checkbox';
import ChevronDownCircleIcon from '@atlaskit/icon/glyph/chevron-down-circle';
import EditorMentionIcon from '@atlaskit/icon/glyph/editor/mention';
import EditorTextStyleIcon from '@atlaskit/icon/glyph/editor/text-style';
import IssueIcon from '@atlaskit/icon/glyph/issue';
import IssuesIcon from '@atlaskit/icon/glyph/issues';
import LabelIcon from '@atlaskit/icon/glyph/label';
import LinkIcon from '@atlaskit/icon/glyph/link';
import MediaServicesScaleLargeIcon from '@atlaskit/icon/glyph/media-services/scale-large';
import PageIcon from '@atlaskit/icon/glyph/page';
import SearchIcon from '@atlaskit/icon/glyph/search';
import ContentDetailsValueView from '@databases/field/content/confluence/ContentDetailsValueView';
import { EntryIconOutline, InfoOutlineIcon, NumberIcon } from '@databases/icons';
import { fieldTypesNames } from '@databases/intl/field-type-names';
import { messages } from '@databases/intl/messages';
import { DefaultRootState } from '@databases/redux';
import {
  DatabaseField,
  DatabaseFieldType,
  RawDatabaseValue,
  ValueWithContext,
} from '@databases/types/database';
import { DatabaseChange, ValueChange } from '@databases/utils/database/changesets';
import { getReferencedLabelContentRefs } from '@databases/utils/database/field-types/pageLabels';
import { getReferencedStatusContentRefs } from '@databases/utils/database/field-types/pageStatus';
import React, { ComponentType } from 'react';

import AttachmentValueEdit from './edit/AttachmentValueEdit';
import CheckboxValueEdit from './edit/CheckboxValueEdit';
import DateValueEdit from './edit/DateValueEdit';
import EntryLinkValueEdit from './edit/EntryLinkValueEdit';
import HyperlinkValueEdit from './edit/HyperlinkValueEdit';
import JiraIssueValueEdit from './edit/JiraIssueValueEdit';
import NumberValueEdit from './edit/NumberValueEdit';
import PageLabelsValueEdit from './edit/PageLabelsValueEdit';
import PageStatusValueEdit from './edit/PageStatusValueEdit';
import SelectValueEdit from './edit/SelectValueEdit';
import TextValueEdit from './edit/TextValueEdit';
import UserValueEdit from './edit/UserValueEdit';
import PageLinkValueEdit from './edit/page-link/PageLinkValueEdit';
import AttachmentValueView from './view/AttachmentValueView';
import BacklinkValueView from './view/BacklinkValueView';
import DateValueView from './view/DateValueView';
import EntryDetailsValueView from './view/EntryDetailsValueView';
import EntryLinkValueView from './view/EntryLinkValueView';
import HyperlinkValueView from './view/HyperlinkValueView';
import JiraIssueDetailsValueView from './view/JiraIssueDetailsValueView';
import JiraIssueValueView from './view/JiraIssueValueView';
import NumberValueView from './view/NumberValueView';
import PageLinkValueView from './view/PageLinkValueView';
import SelectValueView from './view/SelectValueView';
import TextValueView from './view/TextValueView';
import UserValueView from './view/UserValueView';

export enum FieldTypeCategory {
  BASIC = 'Basic',
  PAGE = 'Page',
  JIRA = 'Jira',
  OTHER_DATABASES = 'Other Databases',
}

export enum EditMode {
  /** Values of this type are not editable, the view component is always rendered. */
  READ_ONLY,

  /** Values of this type can switch between the view and edit component when it is focused/blurred. */
  INLINE_EDIT,

  /** The (seamless) edit component of this value should always be rendered (if the database is editable). */
  EDIT,
}

export interface ViewComponentProps<Type extends DatabaseFieldType> {
  contentId: string;
  entryId?: string;
  field: DatabaseField<Type>;
  value: RawDatabaseValue<Type>;
  showPlaceholder?: boolean;
  noFocus?: boolean;
  recursionCount: number;
  /**
   * If this is true, the component is rendered in a read-only context. This should be passed on to any nested contexts.
   * For example, when the component opens an entry dialog, the entry dialog should be opened in read-only mode.
   */
  isReadonly?: boolean;
}

export interface EditComponentProps<Type extends DatabaseFieldType> {
  contentId: string;
  /** edit components are not always used to update an entry. Filtering is an example (FilterValueEdit) */
  entryId?: string;
  field: DatabaseField<Type>;
  value: RawDatabaseValue<Type>;
  onChange: (value: RawDatabaseValue<Type>, change: ValueChange | null) => void;
  onFocus?: (e?: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isInvalid?: boolean;
  isBulkEdit?: string;
  appearance?: 'default' | 'none' | 'subtle' | undefined;

  /** Indicates that we are editing the default value of the field. */
  isDefaultValue?: boolean;

  /** Render the field seamless, so that it looks like a database value and only changes into an edit field on hover/focus. */
  isSeamless?: boolean;

  /**
   * If specified, allows to draft the given database update. This can be used to cause changes outside the value itself.
   * not always specified because edit components are not always used to update the draft data. Filtering is an example (FilterValueEdit)
   * */
  addChangeToDraft?: (change: DatabaseChange) => void;
  placeholder?: string;
}

export interface FieldType<Type extends DatabaseFieldType> {
  icon: ComponentType<GlyphProps>;
  label: { id: string; defaultMessage: string; description: string };
  description?: { id: string; defaultMessage: string; description: string };
  shortDescription?: { id: string; defaultMessage: string; description: string };
  errorDescription?: { id: string; defaultMessage: string; description: string };
  category: FieldTypeCategory;
  view: ComponentType<ViewComponentProps<Type>>;
  popoverEdit?: ComponentType<EditComponentProps<Type>>;
  edit: ComponentType<EditComponentProps<Type>>;
  editMode: (context: ValueWithContext, state: DefaultRootState) => EditMode;
}

export const FIELD_TYPES: { [Type in DatabaseFieldType]: FieldType<Type> } = {
  // Basic

  [DatabaseFieldType.TEXT]: {
    icon: EditorTextStyleIcon,
    label: fieldTypesNames[DatabaseFieldType.TEXT],
    shortDescription: messages['field-settings.type-text-short-description'],
    category: FieldTypeCategory.BASIC,
    view: (props) => <TextValueView {...props} />,
    edit: (props) => <TextValueEdit {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.ATTACHMENT]: {
    icon: MediaServicesScaleLargeIcon,
    label: fieldTypesNames[DatabaseFieldType.ATTACHMENT],
    category: FieldTypeCategory.BASIC,
    view: (props) => <AttachmentValueView {...props} />,
    edit: (props) => <AttachmentValueEdit {...props} />,
    editMode: () => EditMode.EDIT,
  },
  [DatabaseFieldType.NUMBER]: {
    icon: NumberIcon,
    label: fieldTypesNames[DatabaseFieldType.NUMBER],
    category: FieldTypeCategory.BASIC,
    view: (props) => <NumberValueView {...props} />,
    edit: (props) => <NumberValueEdit {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.DATE]: {
    icon: CalendarFilledIcon,
    label: fieldTypesNames[DatabaseFieldType.DATE],
    category: FieldTypeCategory.BASIC,
    view: (props) => <DateValueView {...props} />,
    edit: (props) => <DateValueEdit {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.SELECT]: {
    icon: ChevronDownCircleIcon,
    label: fieldTypesNames[DatabaseFieldType.SELECT],
    shortDescription: messages['field-settings.type-tag-short-description'],
    category: FieldTypeCategory.BASIC,
    view: (props) => <SelectValueView {...props} />,
    edit: (props) => <SelectValueEdit {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.USER]: {
    icon: EditorMentionIcon,
    label: fieldTypesNames[DatabaseFieldType.USER],
    shortDescription: messages['field-settings.type-user-short-description'],
    category: FieldTypeCategory.BASIC,
    view: (props) => <UserValueView {...props} />,
    edit: (props) => <UserValueEdit {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.HYPERLINK]: {
    icon: LinkIcon,
    label: fieldTypesNames[DatabaseFieldType.HYPERLINK],
    shortDescription: messages['field-settings.type-link-short-description'],
    category: FieldTypeCategory.BASIC,
    view: (props) => <HyperlinkValueView {...props} />,
    edit: (props) => <HyperlinkValueEdit {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.CHECKBOX]: {
    icon: CheckboxIcon,
    label: fieldTypesNames[DatabaseFieldType.CHECKBOX],
    shortDescription: messages['field-settings.type-checkbox-short-description'],
    category: FieldTypeCategory.BASIC,
    view: (props) => <CheckboxValueEdit {...props} />,
    edit: (props) => <CheckboxValueEdit recursionCount={0} {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.PAGE_LINK]: {
    icon: PageIcon,
    label: fieldTypesNames[DatabaseFieldType.PAGE_LINK],
    shortDescription: messages['field-settings.type-page-link-short-description'],
    category: FieldTypeCategory.PAGE,
    view: (props) => <PageLinkValueView {...props} />,
    edit: (props) => <PageLinkValueEdit {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.PAGE_STATUS]: {
    icon: CheckCircleOutlineIcon,
    label: fieldTypesNames[DatabaseFieldType.PAGE_STATUS],
    description: messages['field-settings.type-page-status-description'],
    shortDescription: messages['field-settings.type-page-status-short-description'],
    errorDescription: messages['field-settings.type-page-status-error-description'],
    category: FieldTypeCategory.PAGE,
    view: (props) => <ContentDetailsValueView {...props} />,
    edit: (props) => <PageStatusValueEdit {...props} />,
    editMode: ({ database, field, entry }, state) =>
      !field.settings.allowEdit ||
      getReferencedStatusContentRefs({ database, field, entry }, state).length === 0
        ? EditMode.READ_ONLY
        : EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.PAGE_LABELS]: {
    icon: LabelIcon,
    label: fieldTypesNames[DatabaseFieldType.PAGE_LABELS],
    description: messages['field-settings.type-page-label-description'],
    shortDescription: messages['field-settings.type-page-label-short-description'],
    errorDescription: messages['field-settings.type-page-label-error-description'],
    category: FieldTypeCategory.PAGE,
    view: (props) => <ContentDetailsValueView {...props} />,
    edit: (props) => <PageLabelsValueEdit {...props} />,
    editMode: ({ database, field, entry }, state) =>
      !field.settings.allowEdit ||
      getReferencedLabelContentRefs({ database, field, entry }, state).length === 0
        ? EditMode.READ_ONLY
        : EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.PAGE_DETAILS]: {
    icon: InfoOutlineIcon,
    label: fieldTypesNames[DatabaseFieldType.PAGE_DETAILS],
    description: messages['field-settings.type-page-details-description'],
    shortDescription: messages['field-settings.type-page-details-short-description'],
    errorDescription: messages['field-settings.type-page-details-error-description'],

    category: FieldTypeCategory.PAGE,
    view: (props) => <ContentDetailsValueView {...props} />,
    edit: () => null,
    editMode: () => EditMode.READ_ONLY,
  },
  [DatabaseFieldType.ENTRY_LINK]: {
    icon: EntryIconOutline,
    label: fieldTypesNames[DatabaseFieldType.ENTRY_LINK],
    description: messages['field-settings.type-entry-link-description'],
    shortDescription: messages['field-settings.type-entry-link-short-description'],
    category: FieldTypeCategory.OTHER_DATABASES,
    view: (props) => <EntryLinkValueView {...props} />,
    edit: (props) => <EntryLinkValueEdit {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.ENTRY_DETAILS]: {
    icon: SearchIcon,
    label: fieldTypesNames[DatabaseFieldType.ENTRY_DETAILS],
    description: messages['field-settings.type-entry-details-description'],
    shortDescription: messages['field-settings.type-entry-details-short-description'],
    errorDescription: messages['field-settings.type-entry-details-error-description'],
    category: FieldTypeCategory.OTHER_DATABASES,
    view: (props) => <EntryDetailsValueView {...props} />,
    edit: () => null,
    editMode: () => EditMode.READ_ONLY,
  },
  [DatabaseFieldType.BACKLINK]: {
    icon: ArrowLeftCircleIcon,
    label: fieldTypesNames[DatabaseFieldType.BACKLINK],
    description: messages['field-settings.type-entry-backlink-description'],
    shortDescription: messages['field-settings.type-entry-backlink-short-description'],
    category: FieldTypeCategory.OTHER_DATABASES,
    view: (props) => <BacklinkValueView {...props} />,
    edit: () => null,
    editMode: () => EditMode.READ_ONLY,
  },
  [DatabaseFieldType.JIRA_ISSUE]: {
    icon: IssueIcon,
    label: fieldTypesNames[DatabaseFieldType.JIRA_ISSUE],
    shortDescription: messages['field-settings.type-jira-issue-short-description'],
    category: FieldTypeCategory.JIRA,
    view: (props) => <JiraIssueValueView {...props} />,
    edit: (props) => <JiraIssueValueEdit {...props} />,
    editMode: () => EditMode.INLINE_EDIT,
  },
  [DatabaseFieldType.JIRA_ISSUE_DETAILS]: {
    icon: IssuesIcon,
    label: fieldTypesNames[DatabaseFieldType.JIRA_ISSUE_DETAILS],
    description: messages['field-settings.type-jira-issue-details-description'],
    shortDescription:
      messages['field-settings.type-jira-issue-details-short-description'],
    errorDescription:
      messages['field-settings.type-jira-issue-details-error-description'],
    category: FieldTypeCategory.JIRA,
    view: (props) => <JiraIssueDetailsValueView {...props} />,
    edit: (_) => null,
    editMode: () => EditMode.READ_ONLY,
  },
};
